import React, { useEffect, useState } from "react";
import { RouteComponentProps } from "react-router-dom";
import { connect, ConnectedProps } from "react-redux";
import { useForm } from "react-hook-form";
import ReCAPTCHA from "react-google-recaptcha";
import queryString from "query-string";

import { User } from "_types/system";
import { beginSession } from "_store/system/thunks";
import { errorMessage, makeRequest, useRequest } from "_utils/request";

import { Loader, Alert, SessionPane } from "_components";

import "./ResetPassword.sass";

interface EnterEmailProps {
  onSubmit: (email: string) => void;
}

const EnterEmail: React.FC<EnterEmailProps> = (props) => {
  interface FormData {
    email: string;
  }

  const {register, handleSubmit, errors} = useForm<FormData>({defaultValues: {email: ""}});
  const onSubmit = handleSubmit(({email}) => props.onSubmit(email));

  return <form onSubmit={onSubmit}>
    <div className="form-group">
      <div className="form-group">
        <label>Please enter your e-mail address.</label>
        <input name="email" placeholder="e-mail address" className="form-control"
               ref={register({required: true, pattern: /^\S+@\S+\.\S+$/})} />
      </div>
      {errors.email && <div className="form-group">
        <Alert contextualClass="warning">Please enter a valid e-mail address.</Alert>
      </div>}
      <div className="form-group">
        <input type="submit" className="form-control btn btn-primary" value="Continue" />
      </div>
      {/*<p><small><Link to={{*/}
      {/*  pathname: "/login",*/}
      {/*  // TODO: let login keep this data in its global state*/}
      {/*  state: {*/}
      {/*    emailTypedInSoFar: this.state.inputs.email,*/}
      {/*    from: this.props.from,*/}
      {/*  },*/}
      {/*}}>Found your password? Sign in instead</Link></small></p>*/}
    </div>
  </form>
}

interface RequestTokenProps {
  email: string;
  captcha: string;
  onSuccess: () => void;
  retry: () => void;
}

const RequestToken: React.FC<RequestTokenProps> = (props) => {
  type requestTokenError = "invalidCaptcha";
  const requestTokenErrorMessages = new Map<requestTokenError, string>([
    ["invalidCaptcha", "Something went wrong with the captcha."]
  ]);

  const request = makeRequest('/passwordReset', 'POST', {
    email: props.email,
    captchaResponse: props.captcha,
  });
  const [result] = useRequest<void, requestTokenError>(
      request,
      response => (response.status === 400) ? "invalidCaptcha" : null
  );
  const { onSuccess } = props;
  useEffect(() => {
    if (result.kind === "success") onSuccess();
  }, [result.kind, onSuccess]);
  switch(result.kind) {
    case "error":
      return <div>
        <p>{errorMessage(result.error, requestTokenErrorMessages)}</p>
        <button className="btn btn-primary" onClick={props.retry}>Try again</button>
      </div>
    default:
      return <Loader />
  }
}

const RequestPasswordReset: React.FC = () => {
  type EnterEmailState = {kind: "enterEmail"};
  type SolveCaptchaState = {kind: "solveCaptcha", email: string};
  type RequestTokenState = {kind: "requestToken", email: string, captcha: string};
  type ConfirmationState = {kind: "confirmation"};
  let enterEmailState = () => ({kind: "enterEmail"}) as EnterEmailState;
  let solveCaptchaState = (email: string) => ({kind: "solveCaptcha", email}) as SolveCaptchaState;
  let requestTokenState = (email: string, captcha: string) => ({kind: "requestToken", email, captcha}) as RequestTokenState;
  let confirmationState = () => ({kind: "confirmation"}) as ConfirmationState;
  type requestPasswordResetState = EnterEmailState | SolveCaptchaState | RequestTokenState | ConfirmationState;
  const [state, setState] = useState<requestPasswordResetState>(enterEmailState());

  switch (state.kind) {
    case "enterEmail":
      return <EnterEmail onSubmit={(email) => setState(solveCaptchaState(email))} />

    case "solveCaptcha":
      return <ReCAPTCHA className="captcha"
                        sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                        onChange={value => setState(requestTokenState(state.email, value))}
      />

    case "requestToken":
      return <RequestToken {...state}
                           onSuccess={() => setState(confirmationState())}
                           retry={() => setState(enterEmailState())}
      />

    case "confirmation":
      return <p>
        We have received your request.
        If you have a Sol Atelier account, a password reset link will be e-mailed to you shortly.
      </p>
  }
}

interface VerifyTokenProps {
  email: string;
  token: string;
  onSuccess: () => void;
}

const VerifyToken: React.FC<VerifyTokenProps> = (props) => {
  type verifyTokenError = "tokenRejected";
  const verifyTokenErrorMessages = new Map<verifyTokenError, string>([
    ["tokenRejected", "This password reset link is invalid."]
  ]);

  const request = makeRequest('/verifyPasswordReset', 'POST', {email: props.email, resetToken: props.token});
  const [result] = useRequest<{}, verifyTokenError>(
      request,
      (response) => (response.status === 404) ? 'tokenRejected' : null
  );
  const { onSuccess } = props;
  useEffect(() => {
    if (result.kind === "success") onSuccess();
  }, [result.kind, onSuccess]);
  switch (result.kind) {
    case "error":
      return <p>{errorMessage(result.error, verifyTokenErrorMessages)}</p>;
    default:
      return <Loader />
  }
}

interface EnterNewPasswordProps {
  email: string;
  onSubmit: (password: string) => void;
}

const EnterNewPassword: React.FC<EnterNewPasswordProps> = (props) => {
  interface FormData {
    password: string;
    confirmPassword: string;
  }

  const {register, handleSubmit, errors, watch} = useForm<FormData>();
  const onSubmit = handleSubmit(({password}) => props.onSubmit(password));

  return <form onSubmit={onSubmit}>
    <div className="form-group">
      <div className="form-group">
        <input name="email" disabled value={props.email} className="form-control" />
      </div>
      <div className="form-group">
        <label>Please enter a new password.</label>
        <small><p>It must consist of at least 8 lower- and uppercase letters, digits, or special characters.</p></small>
        <input name="password" placeholder="password" type="password" className="form-control"
               ref={register({required: true, pattern: /^.{8,}$/})} />
      </div>
      <div className="form-group">
        <input name="confirmPassword" placeholder="confirm password" type="password" className="form-control"
               ref={register({required: true, validate: value => value === watch("password")})} />
      </div>
      {errors.password && <div className="form-group">
        <Alert contextualClass="warning">Your password does not meet the complexity requirements.</Alert>
      </div>}
      {errors.confirmPassword && <div className="form-group">
        <Alert contextualClass="warning">Passwords do not match.</Alert>
      </div>}
      <div className="form-group">
        <input type="submit" className="form-control btn btn-primary" value="Continue" />
      </div>
    </div>
  </form>
}

interface SubmitNewPasswordProps {
  email: string;
  token: string;
  password: string;
  onSuccess: (user: User) => void;
}

const SubmitNewPassword: React.FC<SubmitNewPasswordProps> = (props) => {
  // Even though the backend can reject the token, it's not normal behaviour since we already checked its validity.
  // Hence, treat it as an unknown error so that it's properly escalated.
  const request = makeRequest('/confirmPasswordReset', 'POST', {
    email: props.email,
    resetToken: props.token,
    newPassword: props.password
  });
  const [result] = useRequest<User>(request);

  const { onSuccess } = props;
  useEffect(() => {
    if (result.kind === "success") onSuccess(result.payload);
  }, [result, onSuccess]);

  switch (result.kind) {
    case "error":
      return <p>{errorMessage(result.error)}</p>;
    default:
      return <Loader />
  }
}

const connector = connect(null, ({beginSession: beginSession}));

interface PConfirmationProps {
  user: User;
  navigateToRoot: () => void;
}

const PConfirmation: React.FC<PConfirmationProps & ConnectedProps<typeof connector>> = (props) => {
  const [keepSignedIn, setKeepSignedIn] = useState<boolean>(false);
  const onSubmit = () => {
    // TODO: ensure we navigate to root *after* User gets stored
    props.beginSession(props.user, keepSignedIn);
    props.navigateToRoot();
  };

  return <div>
    <p>Your password has been successfully changed.</p>
    <form onSubmit={e => e.preventDefault()}><div className="form-group">
      <div className="form-check">
        <input className="form-check-input" name="keepSignedIn" type="checkbox" id="keepSignedInCheckbox"
               checked={keepSignedIn}
               onChange={e => setKeepSignedIn(e.target.checked)}
        />
        <label className="form-check-label" htmlFor="keepSignedInCheckbox">Keep me signed in</label>
      </div>
    </div></form>
    <button className="btn btn-primary" onClick={onSubmit}>Continue</button>
  </div>
}

const Confirmation = connector(PConfirmation);

interface PerformPasswordResetProps {
  email: string;
  token: string;
  navigateToRoot: () => void;
}

const PerformPasswordReset: React.FC<PerformPasswordResetProps> = ({ email, token, navigateToRoot }) => {
  // ensure the user is only prompted for a password with a valid token
  type VerifyTokenState = {kind: "verifyToken"};
  let verifyTokenState = () => ({kind: "verifyToken"}) as VerifyTokenState;

  // prompt the user for a new password and validate its security
  type EnterNewPasswordState = {kind: "enterNewPassword"};
  let enterNewPasswordState = () => ({kind: "enterNewPassword"}) as EnterNewPasswordState;

  // post the new password to the backend
  type SubmitNewPasswordState = {kind: "submitNewPassword", password: string};
  let submitNewPasswordState = (password: string) => ({kind: "submitNewPassword", password}) as SubmitNewPasswordState;

  // inform the user of success and guide them to login
  type ConfirmationState = {kind: "confirmation", user: User};
  let confirmationState = (user: User) => ({kind: "confirmation", user}) as ConfirmationState;

  type State = VerifyTokenState | EnterNewPasswordState | SubmitNewPasswordState | ConfirmationState;
  const [state, setState] = useState<State>(verifyTokenState());

  switch (state.kind) {
    case "verifyToken":
      return <VerifyToken email={email} token={token} onSuccess={() => setState(enterNewPasswordState())} />

    case "enterNewPassword":
      return <EnterNewPassword email={email} onSubmit={(password => setState(submitNewPasswordState(password)))} />

    case "submitNewPassword":
      return <SubmitNewPassword email={email} token={token} password={state.password} onSuccess={(user) => setState(confirmationState(user))} />

    case "confirmation":
      return <Confirmation user={state.user} navigateToRoot={navigateToRoot} />
  }
}

export const ResetPassword: React.FC<RouteComponentProps> = ({ history, location }) => {
  const { email, token } = queryString.parse(location.search);
  // const { emailTypedInSoFar, from } = location.state || {}
  // TODO: location.state -> redux
  return <SessionPane className="reset-password-form">
    <h1>Request a new password</h1>
    {(email && token)
      ? <PerformPasswordReset email={email as string} token={token as string} navigateToRoot={() => history.replace('/')} />
      : <RequestPasswordReset />
    }
  </SessionPane>
}
